import localStorage from '@client/core/utils/localStorage.js';
import {
  Brand,
  ConsentStatus,
  debugLog,
  UNLEASH_FEATURE_NAME,
  type GamKeyValue
} from '@schibsted-nmp/advertising-shared';

import { toDeviceGeo } from './deviceGeo.js';
import { $deviceGeoAtom, $glimrTagsAtom } from '../atoms/config.js';
import { $consentAtom } from '../atoms/consent.js';
import { isFeatureEnabled } from '../atoms/unleashFeatures.js';

// TODO: fix wrong usage of localStorage and array values (localStorage only supports string values - use proper serialization).

export const GLIMR_TAG_STORAGE_KEY = 'advertising-podlet.glimrTags';

/**
 * Glimr SDK 3.4.0 (CDN-only; not published to npm). 3.4.0 adds the
 * `{ coordinates: true }` option to `getTags`, which returns a coarse centroid
 * coordinate as the third callback argument. Loaded client-side so it stays
 * gated behind advertising consent (see `loadGlimrIfPermission`).
 */
const GLIMR_SDK_URL =
  'https://storage.googleapis.com/glimr-static/glimrsdk-js/3.4.0/glimr.min.js';

let glimrSdkPromise: Promise<GlimrSdk> | null = null;

function loadGlimrSdk(): Promise<GlimrSdk> {
  if (window.Glimr) return Promise.resolve(window.Glimr);

  glimrSdkPromise ??= new Promise<GlimrSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GLIMR_SDK_URL;
    script.async = true;

    script.addEventListener('load', () => {
      if (window.Glimr) {
        resolve(window.Glimr);
      } else {
        reject(new Error('Glimr SDK loaded but window.Glimr is undefined'));
      }
    });

    script.addEventListener('error', () => {
      glimrSdkPromise = null;
      reject(new Error('Failed to load Glimr SDK'));
    });

    document.head.appendChild(script);
  });

  return glimrSdkPromise;
}

function getClientIdByBrand(brand: Brand) {
  switch (brand) {
    case Brand.Blocket:
      return 'AM3UZB2HBZHD9R7M55JS';
    case Brand.Tori:
      return 'CQSKY6V5814SQJ61XFJQ';
    case Brand.Finn:
      return 'ESA0AB9PATGZ2AGWICEX';
    case Brand.Dba:
      return 'UYSCXA35QWYC4D4CWBC1';
    default:
      return '';
  }
}

export function clearPersistedGlimrTags() {
  localStorage.clearValue(GLIMR_TAG_STORAGE_KEY);
}

export function getPersistedGlimrTags(): Array<string> {
  try {
    return (
      (localStorage.getItem(GLIMR_TAG_STORAGE_KEY) as Array<string> | null) ??
      []
    );
  } catch {
    return [];
  }
}

export function setPersistedGlimrTags(tags: string[]) {
  $glimrTagsAtom.set(tags);
  localStorage.setItem(GLIMR_TAG_STORAGE_KEY, tags);
}

export async function loadGlimrIfPermission(brand: Brand) {
  try {
    const consent = $consentAtom.get();

    if (consent === ConsentStatus.Accepted) {
      const { tags, coordinate } = await loadGlimr({ brand });
      $glimrTagsAtom.set(tags);
      setPersistedGlimrTags(tags);
      $deviceGeoAtom.set(toDeviceGeo(coordinate));
    } else {
      clearPersistedGlimrTags();
      $deviceGeoAtom.set(null);
    }
  } catch (error) {
    console.error('Error loading glimr', error);
  }
}

export function getGlimrTags(
  tags: ReadonlyArray<string>
): ReadonlyArray<GamKeyValue> {
  const value = Array.from(tags);

  return new Array<GamKeyValue>(
    { key: 'glimr_weather', value },
    { key: 'glimr_geo', value }
  );
}

export async function loadGlimr({
  brand
}: {
  brand: Brand;
}): Promise<loadGlimr.Value> {
  debugLog('Load glimr');

  const clientId = getClientIdByBrand(brand);
  if (!clientId) return { tags: [], tagMappings: {}, coordinate: null };

  const Glimr = await loadGlimrSdk();
  Glimr.setTagCacheTimeInSeconds(300);

  // Only request centroid coordinates when the feature is enabled, so no
  // IP-derived geo is fetched or held while the flag is off.
  const withCoordinates = isFeatureEnabled(
    UNLEASH_FEATURE_NAME.enableDeviceGeo
  );

  const { promise, resolve } = Promise.withResolvers<loadGlimr.Value>();

  Glimr.getTags(
    clientId,
    (tags, tagMappings, coordinate) => {
      resolve({ tags, tagMappings, coordinate });
    },
    { coordinates: withCoordinates }
  );

  return await promise;
}

export namespace loadGlimr {
  export type Value = {
    tags: Array<string>;
    tagMappings: Record<string, unknown>;
    coordinate: GlimrCoordinate | null;
  };
}
